import type { ComponentType } from "react";
import {
  Package,
  TrendingUp,
  Search,
  Monitor,
  Tags,
  Zap,
  MapPin,
  AlertTriangle,
  BellRing,
  ScanLine,
  Crosshair,
  LineChart,
  Building2,
  Camera,
  Smartphone,
} from "lucide-react";

export type LandingFeatureSlug =
  | "in-store-deals"
  | "hot-leads"
  | "product-search"
  | "price-alerts";

export type CardStyle = "product" | "lead" | "tracker";

/**
 * A generic mock product used to render any of the card styles.
 * Notice: never mentions a retailer by name.
 */
export interface MockProduct {
  name: string;
  category: string;
  storeLabel: string; // e.g. "Springfield · #1247" — never includes a retailer name
  upc: string;
  aisle?: string;
  image: string; // path under /feature-images
  msrp: number;
  price: number;
  stock: number;
  badge?: "NEW" | "PRICE↓" | "LOWEST" | "STOCK↑" | "HOT" | "RARE";
  changedAgo: string;
  updatedAgo: string;
  pickupAvailable?: boolean;
  // Tracker-only fields:
  targetPrice?: number;
  zipCode?: string;
  alertHit?: boolean;
}

export interface LandingFeature {
  slug: LandingFeatureSlug;
  navLabel: string;
  navDescription: string;
  icon: ComponentType<{ className?: string; size?: number }>;
  heroEyebrow: string;
  heroTitle: string;
  heroItalic: string;
  heroSubtitle: string;
  ctaLabel: string;
  iconBg: string;
  accentText: string;
  accentBadge: string;
  cardStyle: CardStyle;
  /** Optional hero illustration shown to the right of the hero copy. */
  heroIllustration?: string;
  /** Optional preview image shown on the landing features grid card. */
  previewImage?: string;
  benefits: { icon: ComponentType<{ className?: string }>; title: string; body: string }[];
  steps: { title: string; body: string }[];
  products: MockProduct[];
  faq?: { q: string; a: string }[];
}

export const LANDING_FEATURES: LandingFeature[] = [
  {
    slug: "in-store-deals",
    navLabel: "In-Store Clearance",
    navDescription: "Live markdowns at every store near you",
    icon: Package,
    iconBg: "bg-gradient-to-br from-violet-500 to-fuchsia-500",
    accentText: "text-violet-700",
    accentBadge: "bg-violet-100 text-violet-700",
    cardStyle: "product",
    previewImage: "/feature-images/in-store-clearance-preview.png",
    heroIllustration: "/feature-images/in-store-clearance-preview.png",
    heroEyebrow: "In-store deals",
    heroTitle: "See every clearance",
    heroItalic: "before anyone else",
    heroSubtitle:
      "Browse real-time markdowns at the exact stores nearby. 7+ major retailers — local and online — covered. No more aimless aisle walks.",
    ctaLabel: "Start finding markdowns",
    benefits: [
      {
        icon: Tags,
        title: "Real shelf prices",
        body: "Prices come from each store's own inventory system, so you see the exact tag price you'll see in the aisle.",
      },
      {
        icon: MapPin,
        title: "Aisle-level locations",
        body: "Every deal shows its aisle. Walk straight to the shelf — no scavenger hunt.",
      },
      {
        icon: Zap,
        title: "Fresh every few minutes",
        body: "Stores are re-scanned constantly. New markdowns show up before staff even update the signs.",
      },
    ],
    steps: [
      {
        title: "Pick your retailers",
        body: "Subscribe to any combination of supported retailers — local big-box, hardware, online, and warehouse clubs.",
      },
      {
        title: "Set your local stores",
        body: "Lock in the specific store numbers near you and we'll only show those deals.",
      },
      {
        title: "Browse fresh markdowns",
        body: "Sort by discount, savings, recently changed, or 'just hit lowest ever'.",
      },
    ],
    products: [
      {
        name: "8-Quart Electric Pressure Cooker",
        category: "Kitchen",
        storeLabel: "Springfield · #1247",
        upc: "622356527788",
        aisle: "A12",
        image: "/feature-images/product-pressure-cooker.png",
        msrp: 149.0,
        price: 38.0,
        stock: 4,
        badge: "PRICE↓",
        changedAgo: "12m ago",
        updatedAgo: "3m ago",
        pickupAvailable: true,
      },
      {
        name: "Cordless Stick Vacuum Cleaner",
        category: "Home",
        storeLabel: "Plano · #2891",
        upc: "885155014849",
        aisle: "B04",
        image: "/feature-images/product-vacuum.png",
        msrp: 399.99,
        price: 199.98,
        stock: 1,
        badge: "LOWEST",
        changedAgo: "1h ago",
        updatedAgo: "4m ago",
      },
      {
        name: "65-inch 4K QLED Smart Television",
        category: "Electronics",
        storeLabel: "Austin · #482",
        upc: "027242926011",
        image: "/feature-images/product-tv.png",
        msrp: 1199.99,
        price: 649.99,
        stock: 3,
        badge: "PRICE↓",
        changedAgo: "23m ago",
        updatedAgo: "8m ago",
      },
      {
        name: "20V Cordless Drill / Driver Kit",
        category: "Tools",
        storeLabel: "Atlanta · #6342",
        upc: "885911674119",
        aisle: "Bay 14",
        image: "/feature-images/product-drill.png",
        msrp: 199.0,
        price: 79.0,
        stock: 6,
        badge: "NEW",
        changedAgo: "5m ago",
        updatedAgo: "5m ago",
      },
      {
        name: "Sci-Fi Building Block Set",
        category: "Toys",
        storeLabel: "Tulsa · #872",
        upc: "673419355131",
        aisle: "G07",
        image: "/feature-images/product-lego.png",
        msrp: 169.99,
        price: 84.0,
        stock: 2,
        badge: "STOCK↑",
        changedAgo: "44m ago",
        updatedAgo: "2m ago",
      },
      {
        name: "Single-Serve Pod Coffee Maker",
        category: "Kitchen",
        storeLabel: "Boulder · #1132",
        upc: "611247373682",
        aisle: "D11",
        image: "/feature-images/product-coffee-maker.png",
        msrp: 169.99,
        price: 49.98,
        stock: 5,
        badge: "PRICE↓",
        changedAgo: "2h ago",
        updatedAgo: "6m ago",
      },
    ],
    faq: [
      {
        q: "Are these the real in-store prices?",
        a: "Yes — prices come directly from each retailer's store inventory feeds, so what you see is what's on the shelf tag.",
      },
      {
        q: "How fast do new markdowns show up?",
        a: "Most stores get re-scanned every few minutes. New markdowns typically appear within 5–15 minutes of being entered.",
      },
    ],
  },
  {
    slug: "hot-leads",
    navLabel: "Hot Leads",
    navDescription: "Trending deals our community is flipping",
    icon: TrendingUp,
    iconBg: "bg-gradient-to-br from-orange-500 to-rose-500",
    accentText: "text-orange-700",
    accentBadge: "bg-orange-100 text-orange-700",
    cardStyle: "lead",
    previewImage: "/feature-images/hot-leads-preview.png",
    heroIllustration: "/feature-images/hot-leads-preview.png",
    heroEyebrow: "Hot leads",
    heroTitle: "The best deals,",
    heroItalic: "served on a plate",
    heroSubtitle:
      "Our team and community curate the hottest, highest-margin clearance leads in the country across 7+ retailers, local and online. Skip the digging — jump straight to the wins.",
    ctaLabel: "See today's hot leads",
    benefits: [
      {
        icon: Crosshair,
        title: "Vetted by humans, ranked by data",
        body: "Every lead is scored on margin, sell-through, and rarity so you only see what's worth your time.",
      },
      {
        icon: ScanLine,
        title: "One-tap nearby check",
        body: "Tap any lead to instantly check stock and price at every store within driving distance.",
      },
      {
        icon: BellRing,
        title: "Fresh leads, all day long",
        body: "New leads drop hour after hour. Filter by category, retailer, or minimum margin.",
      },
    ],
    steps: [
      {
        title: "Open the leads feed",
        body: "Curated hot leads land at the top — sorted by recency and heat.",
      },
      {
        title: "Tap to check nearby",
        body: "One tap shows you every store within range carrying the item.",
      },
      {
        title: "Run, scan, profit",
        body: "Drive to the right store, scan to confirm, walk out with a winner.",
      },
    ],
    products: [
      {
        name: "Next-Gen Disc Video Game Console",
        category: "Gaming",
        storeLabel: "Multiple stores",
        upc: "711719577232",
        image: "/feature-images/product-console.png",
        msrp: 499.99,
        price: 248.0,
        stock: 12,
        badge: "HOT",
        changedAgo: "8m",
        updatedAgo: "2m",
      },
      {
        name: "Premium Wireless Noise-Cancel Earbuds",
        category: "Audio",
        storeLabel: "Multiple stores",
        upc: "194253397397",
        image: "/feature-images/product-earbuds.png",
        msrp: 249.0,
        price: 89.0,
        stock: 28,
        badge: "PRICE↓",
        changedAgo: "21m",
        updatedAgo: "4m",
      },
      {
        name: "Robot Vacuum with Self-Empty Dock",
        category: "Home",
        storeLabel: "Multiple stores",
        upc: "885155017123",
        image: "/feature-images/product-robot-vacuum.png",
        msrp: 799.99,
        price: 349.99,
        stock: 4,
        badge: "RARE",
        changedAgo: "1h",
        updatedAgo: "11m",
      },
      {
        name: "Insulated 40oz Travel Tumbler",
        category: "Outdoors",
        storeLabel: "Multiple stores",
        upc: "184501030011",
        image: "/feature-images/product-tumbler.png",
        msrp: 45.0,
        price: 14.98,
        stock: 47,
        badge: "HOT",
        changedAgo: "32m",
        updatedAgo: "1m",
      },
      {
        name: "Cordless Wet/Dry Floor Cleaner",
        category: "Home",
        storeLabel: "Multiple stores",
        upc: "011120251401",
        image: "/feature-images/product-wet-vac.png",
        msrp: 299.99,
        price: 79.0,
        stock: 8,
        badge: "LOWEST",
        changedAgo: "18m",
        updatedAgo: "3m",
      },
      {
        name: "Hybrid Handheld Game System Bundle",
        category: "Gaming",
        storeLabel: "Multiple stores",
        upc: "045496883416",
        image: "/feature-images/product-handheld.png",
        msrp: 349.99,
        price: 199.0,
        stock: 6,
        badge: "HOT",
        changedAgo: "47m",
        updatedAgo: "7m",
      },
    ],
    faq: [
      {
        q: "How are leads ranked?",
        a: "We score margin, historical sell-through, and how recently the price moved. The hottest, highest-confidence deals float to the top.",
      },
      {
        q: "Can I save leads to come back to?",
        a: "Yes — every lead has a save button. Saved leads sync across mobile and web instantly.",
      },
    ],
  },
  {
    slug: "product-search",
    navLabel: "Product Search",
    navDescription: "Scan or search any product — see every nearby store",
    icon: Search,
    iconBg: "bg-gradient-to-br from-fuchsia-500 to-pink-500",
    accentText: "text-fuchsia-700",
    accentBadge: "bg-fuchsia-100 text-fuchsia-700",
    cardStyle: "product",
    previewImage: "/feature-images/product-search-preview.png",
    heroIllustration: "/feature-images/product-search-preview.png",
    heroEyebrow: "Product search",
    heroTitle: "Scan one item,",
    heroItalic: "find it everywhere",
    heroSubtitle:
      "Type a UPC / SKU or scan a barcode with your phone. We instantly check every nearby store across 7+ retailers and rank them by price and stock.",
    ctaLabel: "Try the search",
    benefits: [
      {
        icon: Camera,
        title: "Scan with your camera",
        body: "Open the scanner, point at any product tag, and get every nearby price in seconds.",
      },
      {
        icon: Smartphone,
        title: "Or paste a UPC / SKU",
        body: "Already have the code? Drop it in the search bar — same instant results.",
      },
      {
        icon: Building2,
        title: "Works across retailers",
        body: "Cross-reference 7+ retailers — both local and online — in one search.",
      },
    ],
    steps: [
      {
        title: "Search or scan",
        body: "Enter a UPC/SKU or scan a barcode with your phone's camera.",
      },
      {
        title: "See every nearby store",
        body: "Each store shows its price, stock count, aisle, and distance.",
      },
      {
        title: "Drive to the best one",
        body: "Tap any store to open turn-by-turn directions.",
      },
    ],
    products: [
      {
        name: "Premium Wireless Noise-Cancel Earbuds",
        category: "Audio",
        storeLabel: "Best price · Springfield · #1247",
        upc: "194644098245",
        aisle: "Electronics 4A",
        image: "/feature-images/product-earbuds.png",
        msrp: 149.99,
        price: 24.0,
        stock: 6,
        badge: "LOWEST",
        changedAgo: "5m ago",
        updatedAgo: "1m ago",
      },
      {
        name: "Cordless Stick Vacuum Cleaner",
        category: "Home",
        storeLabel: "Denver · #891",
        upc: "885155014849",
        aisle: "B04",
        image: "/feature-images/product-vacuum.png",
        msrp: 399.99,
        price: 189.0,
        stock: 3,
        badge: "PRICE↓",
        changedAgo: "22m ago",
        updatedAgo: "4m ago",
      },
      {
        name: "20V Cordless Drill / Driver Kit",
        category: "Tools",
        storeLabel: "Nashville · #203",
        upc: "885911674119",
        aisle: "Bay 14",
        image: "/feature-images/product-drill.png",
        msrp: 199.0,
        price: 79.0,
        stock: 8,
        badge: "NEW",
        changedAgo: "1h ago",
        updatedAgo: "8m ago",
      },
      {
        name: "Single-Serve Pod Coffee Maker",
        category: "Kitchen",
        storeLabel: "Phoenix · #551",
        upc: "611247373682",
        aisle: "D11",
        image: "/feature-images/product-coffee-maker.png",
        msrp: 169.99,
        price: 49.98,
        stock: 5,
        badge: "PRICE↓",
        changedAgo: "3h ago",
        updatedAgo: "12m ago",
      },
    ],
    faq: [
      {
        q: "Can I scan barcodes?",
        a: "Yes. The mobile app uses your camera to scan UPCs and SKUs and instantly searches every nearby store.",
      },
      {
        q: "How accurate is the nearby price?",
        a: "Prices are pulled live from each store's inventory system. Most stores update within minutes.",
      },
    ],
  },
  {
    slug: "price-alerts",
    navLabel: "Price Alerts",
    navDescription: "Get pinged the second a target price drops",
    icon: Monitor,
    iconBg: "bg-gradient-to-br from-amber-500 to-orange-500",
    accentText: "text-amber-700",
    accentBadge: "bg-amber-100 text-amber-700",
    cardStyle: "tracker",
    previewImage: "/feature-images/price-alerts-preview.png",
    heroIllustration: "/feature-images/price-alerts-preview.png",
    heroEyebrow: "Price alerts",
    heroTitle: "Never miss a markdown,",
    heroItalic: "never refresh again",
    heroSubtitle:
      "Set a target price on any product. We watch 7+ retailers — both local and online — and ping you the instant a store hits your number.",
    ctaLabel: "Set your first alert",
    benefits: [
      {
        icon: BellRing,
        title: "Real-time, not next-day",
        body: "We watch every scan. The alert leaves our servers within seconds of the price hitting your target.",
      },
      {
        icon: LineChart,
        title: "Full price history",
        body: "Every tracked item shows full price + stock history so you know what a 'real' deal looks like.",
      },
      {
        icon: AlertTriangle,
        title: "Smart thresholds",
        body: "Use absolute price, percent off, or 'lowest ever' — and stack as many alerts as you want.",
      },
    ],
    steps: [
      {
        title: "Track any product",
        body: "Tap the alert button on any deal or paste a UPC/SKU to start tracking.",
      },
      {
        title: "Set your target",
        body: "Pick a price, a percent off, or alert me on any drop.",
      },
      {
        title: "Get the ping",
        body: "We notify the second a nearby store crosses your line.",
      },
    ],
    products: [
      {
        name: "11-inch Tablet · 128GB",
        category: "Electronics",
        storeLabel: "Tracking · 3 stores",
        upc: "194253951178",
        image: "/feature-images/product-tablet.png",
        msrp: 599.0,
        price: 449.0,
        targetPrice: 475.0,
        zipCode: "78704",
        stock: 5,
        alertHit: true,
        changedAgo: "Alert hit · 2m ago",
        updatedAgo: "2m ago",
      },
      {
        name: "Over-Ear Noise-Cancelling Wireless Headphones",
        category: "Audio",
        storeLabel: "Tracking · 5 stores",
        upc: "017817844222",
        image: "/feature-images/product-headphones.png",
        msrp: 429.0,
        price: 279.0,
        targetPrice: 299.0,
        zipCode: "30309",
        stock: 2,
        alertHit: true,
        changedAgo: "Alert hit · 14m ago",
        updatedAgo: "5m ago",
      },
      {
        name: "Professional Touchscreen Blender",
        category: "Kitchen",
        storeLabel: "Tracking · 2 stores",
        upc: "703113616723",
        image: "/feature-images/product-blender.png",
        msrp: 649.95,
        price: 329.0,
        targetPrice: 350.0,
        zipCode: "60614",
        stock: 1,
        alertHit: true,
        changedAgo: "Alert hit · 38m ago",
        updatedAgo: "7m ago",
      },
      {
        name: "Cordless Stick Vacuum Cleaner",
        category: "Home",
        storeLabel: "Tracking · 4 stores",
        upc: "027242922488",
        image: "/feature-images/product-vacuum.png",
        msrp: 399.99,
        price: 325.0,
        targetPrice: 250.0,
        zipCode: "98101",
        stock: 4,
        alertHit: false,
        changedAgo: "Checked 9m ago",
        updatedAgo: "9m ago",
      },
    ],
    faq: [
      {
        q: "How fast are alerts delivered?",
        a: "Within seconds of the price hitting your target. We watch every scan and push the alert immediately.",
      },
      {
        q: "Where do alerts go?",
        a: "You can opt-in to push notifications, email, and Discord DMs — any combination.",
      },
    ],
  },
];

export function getFeatureBySlug(slug: string): LandingFeature | undefined {
  return LANDING_FEATURES.find((f) => f.slug === slug);
}
